import { track } from './track';

/**
 * 화면 오류 기록 (2026-10-10 사장님 승인 a-qa-sentry): Sentry 가 한도 초과(429·403)로 받지 않아 끄고,
 * 이미 있는 이용 기록(/api/track → usage_events)에 'err:<화면>:<오류 요약>-<해시>' 로 남긴다.
 * - 서버는 이벤트 이름을 [a-z0-9_:.-] 40자까지만 받으므로 오류 문장을 줄여 넣고, 끝에 전체 문장의 짧은 해시를 붙여 구분한다.
 * - 넘치지 않게: 같은 오류는 세션(탭)마다 1번, 세션당 최대 10개. 확장 프로그램·다른 사이트 스크립트 오류는 버린다.
 * 주간 품질 점검에서 usage_events 의 event LIKE 'err:%' 를 세어 순위를 본다 (tools/qa/KNOWLEDGE.md).
 */
const MAX_PER_SESSION = 10;
const SEEN_KEY = 'csErrSeen';

const IGNORE = [
  /ResizeObserver loop/i,
  /^Script error\.?$/i,
  /chrome-extension:|moz-extension:|safari-extension:/i,
  /Non-Error promise rejection captured/i,
];

const PAGES: [RegExp, string][] = [
  [/^\/$/, 'home'], [/^\/notes\//, 'score'], [/^\/search/, 'search'], [/^\/full-sheet-music/, 'all'],
  [/^\/playlist/, 'playlist'], [/^\/chart/, 'chart'], [/^\/recent/, 'recent'], [/^\/tuner/, 'tuner'],
  [/^\/request/, 'request'], [/^\/hotclip/, 'hotclip'], [/^\/my-scores/, 'myscore'], [/^\/support/, 'support'],
  [/^\/auth|^\/login/, 'auth'], [/^\/billing|^\/pricing|^\/membership/, 'pay'], [/^\/faq/, 'faq'],
];

const pageName = () => {
  const p = window.location.pathname;
  const hit = PAGES.find(([re]) => re.test(p));
  return hit ? hit[1] : 'etc';
};

const hash6 = (s: string) => {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36).slice(0, 6).padStart(6, '0');
};

const seen = (): string[] => {
  try {
    return JSON.parse(sessionStorage.getItem(SEEN_KEY) || '[]');
  } catch (e) {
    return [];
  }
};

export const reportError = (message: string, source?: string) => {
  try {
    const msg = String(message || '').replace(/^Uncaught\s+/i, '').trim();
    if (!msg || IGNORE.some((re) => re.test(msg) || (source ? re.test(source) : false))) return;
    const page = pageName();
    const h = hash6(page + '|' + msg);
    const list = seen();
    if (list.includes(h) || list.length >= MAX_PER_SESSION) return;
    list.push(h);
    try {
      sessionStorage.setItem(SEEN_KEY, JSON.stringify(list));
    } catch (e) {}
    const head = `err:${page}:`;
    const room = 40 - head.length - 7; // '-' + 해시 6자리
    const slug = msg.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, Math.max(0, room)).replace(/-+$/, '') || 'x';
    track(`${head}${slug}-${h}`);
  } catch (e) {}
};

export const installErrorReport = () => {
  try {
    window.addEventListener('error', (ev: ErrorEvent) => {
      // 그림·스크립트 파일을 못 불러온 경우(ev.message 없음)는 빼고, 실제 스크립트 오류만
      if (!ev || !ev.message) return;
      reportError(ev.message, ev.filename);
    });
    window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
      const r: any = ev && ev.reason;
      // 서버 응답 오류(axios)는 화면 오류가 아니라 서버 쪽 기록에 남으므로 상태 코드만 짧게
      if (r && r.isAxiosError) {
        const st = r.response ? r.response.status : 'net';
        reportError(`api-${st}`);
        return;
      }
      reportError(r && r.message ? `${r.name || 'Error'}: ${r.message}` : String(r));
    });
  } catch (e) {}
};
